
import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { ThemeToggle } from './theme/ThemeToggle';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when changing routes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);
  
  const isHomePage = location.pathname === '/';
  
  const homepageNavLinks = [
    { name: "About", href: "#about" },
    { name: "Skills", href: "#skills" },
    { name: "Experience", href: "#experience" },
    { name: "Education", href: "#education" },
    { name: "Personal", href: "#personal" },
    { name: "Contact", href: "#contact" }
  ];
  
  const mainNavLinks = [
    { name: "Home", href: "/" },
    { name: "Resume", href: "/resume" }
  ];
  
  const navLinks = isHomePage ? homepageNavLinks : mainNavLinks;
  
  const scrollToSection = (href: string) => {
    setIsMenuOpen(false);
    
    if (href.startsWith('#')) {
      // On the homepage, scroll to section
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-background shadow-md py-2' : 'bg-transparent py-3 md:py-5'
    }`}>
      <div className="container max-w-6xl mx-auto px-4 flex items-center justify-between">
        <div>
          <Link to="/">
            <h1 className={`font-serif font-medium transition-all ${
              scrolled ? 'text-lg md:text-xl text-foreground' : 'text-xl md:text-2xl text-foreground'
            }`}>
              Payton Hunter
            </h1>
          </Link>
        </div>
        
        <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
          {navLinks.map((link, index) => (
            link.href.startsWith('#') ? (
              <button
                key={index}
                onClick={() => scrollToSection(link.href)}
                className={`text-sm font-medium hover:text-primary transition-colors ${
                  scrolled ? 'text-foreground' : 'text-foreground'
                }`}
              >
                {link.name}
              </button>
            ) : (
              <Link
                key={index}
                to={link.href}
                className={`text-sm font-medium hover:text-primary transition-colors ${
                  scrolled ? 'text-foreground' : 'text-foreground'
                } ${location.pathname === link.href ? 'font-semibold text-primary' : ''}`}
              >
                {link.name}
              </Link>
            )
          ))}

          {/* Add the theme toggle */}
          <ThemeToggle />
        </div>
        
        <div className="md:hidden flex items-center gap-2">
          {/* Add theme toggle to mobile view */}
          <ThemeToggle />
          
          <button 
            className="text-foreground focus:outline-none p-1"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile menu with improved animation and touch targets */}
      {isMenuOpen && (
        <div className="md:hidden bg-background shadow-lg absolute top-full left-0 w-full animate-fade-in">
          <div className="container mx-auto py-2">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link, index) => (
                link.href.startsWith('#') ? (
                  <button
                    key={index}
                    onClick={() => scrollToSection(link.href)}
                    className="text-foreground hover:text-primary hover:bg-muted py-3 px-6 text-left rounded-md active:bg-accent transition-colors"
                  >
                    {link.name}
                  </button>
                ) : (
                  <Link
                    key={index}
                    to={link.href}
                    className={`text-foreground hover:text-primary hover:bg-muted py-3 px-6 text-left rounded-md active:bg-accent transition-colors ${
                      location.pathname === link.href ? 'font-semibold bg-muted' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              
              {/* If not on the homepage, show the homepage links in mobile menu */}
              {!isHomePage && homepageNavLinks.map((link, index) => (
                <Link
                  key={`home-${index}`}
                  to={`/${link.href}`}
                  className="text-foreground hover:text-primary hover:bg-muted py-3 px-6 text-left rounded-md active:bg-accent transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
